import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import * as api from '../../services/api';
import { useI18n } from '../../i18n/useI18n';

type Props = {
  /** Nếu có: admin quản lý nhân viên của user này. Không có: user tự quản lý. */
  ownerUserId?: string;
};

const UserStaffManager: React.FC<Props> = ({ ownerUserId }) => {
  const { admin: a } = useI18n();
  const t = a.userStaffManagement;
  const [staff, setStaff] = useState<api.StaffUserAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState<api.StaffUserAccount | null>(null);
  const [createForm, setCreateForm] = useState({ email: '', password: '', username: '' });
  const [editForm, setEditForm] = useState({ email: '', username: '', password: '' });
  const [saving, setSaving] = useState(false);

  const stats = useMemo(() => {
    const total = staff.length;
    const active = staff.filter((s) => s.status === 'ACTIVE').length;
    const blocked = total - active;
    return { total, active, blocked };
  }, [staff]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const data = ownerUserId
        ? await api.getUserStaffUsers(ownerUserId)
        : await api.getMyStaffUsers();
      setStaff(data);
    } catch (error) {
      console.error(error);
      toast.error(t.loadFail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerUserId]);

  const handleCreate = async () => {
    if (!createForm.email.trim() || !createForm.password) {
      toast.error(t.emailPasswordRequired);
      return;
    }
    try {
      setSaving(true);
      const payload = {
        email: createForm.email.trim(),
        password: createForm.password,
        ...(createForm.username.trim() ? { username: createForm.username.trim() } : {}),
      };
      if (ownerUserId) await api.createUserStaffUser(ownerUserId, payload);
      else await api.createMyStaffUser(payload);
      toast.success(t.createOk);
      setCreateOpen(false);
      setCreateForm({ email: '', password: '', username: '' });
      await fetchStaff();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t.createFail);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      const data: { username?: string; email?: string; password?: string } = {
        username: editForm.username.trim(),
        email: editForm.email.trim(),
      };
      if (editForm.password) data.password = editForm.password;
      if (ownerUserId) await api.updateUserStaffUser(ownerUserId, selected._id, data);
      else await api.updateMyStaffUser(selected._id, data);
      toast.success(t.updateOk);
      setEditOpen(false);
      setSelected(null);
      await fetchStaff();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t.updateFail);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row: api.StaffUserAccount) => {
    try {
      if (ownerUserId) await api.toggleUserStaffUserStatus(ownerUserId, row._id);
      else await api.toggleMyStaffUserStatus(row._id);
      await fetchStaff();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t.toggleFail);
    }
  };

  const openEdit = (row: api.StaffUserAccount) => {
    setSelected(row);
    setEditForm({ email: row.email, username: row.username, password: '' });
    setEditOpen(true);
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2.5,
      bgcolor: alpha('#f48fb1', 0.04),
    },
  };

  const statusChip = (row: api.StaffUserAccount, compact = false) => (
    <Tooltip title={row.status === 'ACTIVE' ? t.block : t.activate}>
      <Chip
        size="small"
        label={row.status}
        color={row.status === 'ACTIVE' ? 'success' : 'error'}
        onClick={() => void handleToggle(row)}
        sx={{
          fontWeight: 700,
          cursor: 'pointer',
          flexShrink: 0,
          ...(compact
            ? { height: 22, fontSize: '0.65rem' }
            : {}),
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            boxShadow: (theme) =>
              `0 0 0 3px ${alpha(
                row.status === 'ACTIVE' ? theme.palette.error.main : theme.palette.success.main,
                0.18,
              )}`,
            transform: 'translateY(-1px)',
          },
        }}
      />
    </Tooltip>
  );

  return (
    <Box
      sx={{
        '@keyframes usmSoftIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          mb: { xs: 1.5, md: 2.5 },
          p: { xs: 2, md: 2.75 },
          borderRadius: 4,
          color: 'common.white',
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 45%, ${theme.palette.secondary.main} 100%)`,
          boxShadow: (theme) => `0 18px 40px ${alpha(theme.palette.primary.main, 0.26)}`,
          animation: 'usmSoftIn 0.45s ease both',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.18)',
                flexShrink: 0,
              }}
            >
              <GroupsIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  mb: 0.35,
                }}
              >
                {t.title}
              </Typography>
              <Typography sx={{ opacity: 0.92, fontSize: { xs: '0.8rem', md: '0.9rem' }, maxWidth: 560 }}>
                {t.subtitle}
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              alignSelf: { xs: 'stretch', sm: 'center' },
              bgcolor: 'common.white',
              color: 'primary.main',
              borderRadius: 999,
              px: 2.5,
              py: 1.1,
              fontWeight: 800,
              textTransform: 'none',
              boxShadow: '0 10px 22px rgba(0,0,0,0.14)',
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            {t.add}
          </Button>
        </Stack>
      </Paper>

      <Box
        sx={{
          mb: { xs: 1.5, md: 2.5 },
          display: 'grid',
          gap: { xs: 1, sm: 1.5 },
          gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', md: 'repeat(3, 1fr)' },
        }}
      >
        {[
          {
            label: t.statTotal,
            shortLabel: t.statTotalShort,
            value: stats.total,
            tone: 'primary.main',
            bg: (theme: { palette: { primary: { main: string } } }) =>
              alpha(theme.palette.primary.main, 0.08),
          },
          {
            label: t.statActive,
            shortLabel: t.statActiveShort,
            value: stats.active,
            tone: 'success.main',
            bg: (theme: { palette: { success: { main: string } } }) =>
              alpha(theme.palette.success.main, 0.1),
          },
          {
            label: t.statBlocked,
            shortLabel: t.statBlockedShort,
            value: stats.blocked,
            tone: 'error.main',
            bg: (theme: { palette: { error: { main: string } } }) =>
              alpha(theme.palette.error.main, 0.1),
          },
        ].map((item, index) => (
          <Card
            key={item.label}
            elevation={0}
            sx={{
              borderRadius: { xs: 2, md: 3 },
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: item.bg,
              minWidth: 0,
              animation: 'usmSoftIn 0.4s ease both',
              animationDelay: `${0.05 + index * 0.05}s`,
            }}
          >
            <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: { xs: 0.25, sm: 0.75 },
                  fontSize: { xs: '0.65rem', sm: '0.85rem' },
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  {item.shortLabel}
                </Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {item.label}
                </Box>
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: item.tone,
                  fontSize: { xs: '1.15rem', sm: '1.85rem' },
                  lineHeight: 1.2,
                }}
              >
                {loading ? '—' : item.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          animation: 'usmSoftIn 0.45s ease both',
          animationDelay: '0.18s',
        }}
      >
        <Box
          sx={{
            px: { xs: 1.75, md: 2.25 },
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {t.listTitle}
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={7}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Mobile cards */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5 }}>
              {staff.length === 0 ? (
                <Box sx={{ py: 5, textAlign: 'center', px: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      mx: 'auto',
                      mb: 1.5,
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                      color: 'primary.main',
                    }}
                  >
                    <GroupsIcon />
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {t.empty}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t.emptyHint}
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.25}>
                  {staff.map((row, index) => (
                    <Box
                      key={row._id}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        animation: 'usmSoftIn 0.35s ease both',
                        animationDelay: `${0.22 + Math.min(index, 10) * 0.04}s`,
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: 'primary.main',
                            fontSize: '1.05rem',
                            flexShrink: 0,
                          }}
                        >
                          {(row.username || row.email || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.username || '—'}
                            </Typography>
                            {statusChip(row, true)}
                          </Stack>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              mt: 0.25,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.8rem',
                            }}
                          >
                            {row.email}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mt: 1.25 }}
                          >
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {row.createdAt
                                ? format(new Date(row.createdAt), 'dd/MM/yyyy HH:mm')
                                : '—'}
                            </Typography>
                            <Tooltip title={t.edit}>
                              <IconButton
                                size="small"
                                onClick={() => openEdit(row)}
                                sx={{
                                  bgcolor: alpha('#f48fb1', 0.12),
                                  '&:hover': { bgcolor: alpha('#f48fb1', 0.18) },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Desktop table */}
            <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: 'grey.50',
                      '& .MuiTableCell-head': {
                        fontWeight: 700,
                        color: 'text.primary',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    <TableCell sx={{ py: 1.75 }}>{t.colUsername}</TableCell>
                    <TableCell sx={{ py: 1.75 }}>{t.colEmail}</TableCell>
                    <TableCell sx={{ py: 1.75 }}>{t.colStatus}</TableCell>
                    <TableCell sx={{ py: 1.75 }}>{t.colCreatedAt}</TableCell>
                    <TableCell sx={{ py: 1.75 }} align="right">
                      {t.colActions}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staff.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                          <Avatar
                            sx={{
                              width: 56,
                              height: 56,
                              mx: 'auto',
                              mb: 1.5,
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                              color: 'primary.main',
                            }}
                          >
                            <GroupsIcon />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            {t.empty}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {t.emptyHint}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    staff.map((row, index) => (
                      <TableRow
                        key={row._id}
                        sx={{
                          animation: 'usmSoftIn 0.35s ease both',
                          animationDelay: `${0.22 + Math.min(index, 10) * 0.04}s`,
                          '& .MuiTableCell-body': {
                            py: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          },
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.035),
                          },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: 'primary.main',
                                fontSize: '0.95rem',
                              }}
                            >
                              {(row.username || row.email || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600}>
                              {row.username || '—'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{row.email}</TableCell>
                        <TableCell>{statusChip(row)}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {row.createdAt
                            ? format(new Date(row.createdAt), 'dd/MM/yyyy HH:mm')
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t.edit}>
                            <IconButton
                              size="small"
                              onClick={() => openEdit(row)}
                              sx={{
                                bgcolor: alpha('#f48fb1', 0.1),
                                '&:hover': { bgcolor: alpha('#f48fb1', 0.16) },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{t.createTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t.email}
              type="email"
              required
              value={createForm.email}
              onChange={(e) => setCreateForm((s) => ({ ...s, email: e.target.value }))}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t.password}
              type="password"
              required
              value={createForm.password}
              onChange={(e) => setCreateForm((s) => ({ ...s, password: e.target.value }))}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t.usernameOptional}
              value={createForm.username}
              onChange={(e) => setCreateForm((s) => ({ ...s, username: e.target.value }))}
              fullWidth
              helperText={t.usernameHint}
              sx={fieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {t.cancel}
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => void handleCreate()}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, px: 2.5 }}
          >
            {t.create}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{t.editTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t.username}
              value={editForm.username}
              onChange={(e) => setEditForm((s) => ({ ...s, username: e.target.value }))}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t.email}
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t.newPassword}
              type="password"
              value={editForm.password}
              onChange={(e) => setEditForm((s) => ({ ...s, password: e.target.value }))}
              fullWidth
              helperText={t.newPasswordHint}
              sx={fieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {t.cancel}
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => void handleEdit()}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, px: 2.5 }}
          >
            {t.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserStaffManager;
